import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  activityCategory,
  mobilityVehicle,
  foodCategory,
  userActivityEntry,
  userFoodEntry,
  userMobilityEntry,
} from 'src/app/shared/models/interfaces';
import { environment } from 'src/environments/environment';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class DailyService {
  private foodEntriesUrl = `${environment.api}/entries/userFoodEntries`;
  private foodDataUrl = `${environment.api}/daily/food`;
  private activityEntriesUrl = `${environment.api}/entries/userActivityEntries`;
  private activityDataUrl = `${environment.api}/daily/activities`;
  private mobilityEntriesUrl = `${environment.api}/entries/userMobilityEntries`;
  private mobilityDataUrl = `${environment.api}/daily/mobility`;


  // Shared state for user entries
  private userFoodEntriesSubject = new BehaviorSubject<userFoodEntry[]>([]);
  userFoodEntries$: Observable<userFoodEntry[]> = this.userFoodEntriesSubject.asObservable();

  private userActivityEntriesSubject = new BehaviorSubject<userActivityEntry[]>([]);
  userActivityEntries$: Observable<userActivityEntry[]> = this.userActivityEntriesSubject.asObservable();

  private userMobilityEntriesSubject = new BehaviorSubject<userMobilityEntry[]>([]);
  userMobilityEntries$: Observable<userMobilityEntry[]> = this.userMobilityEntriesSubject.asObservable();

  private foodCategoriesSubject = new BehaviorSubject<foodCategory[]>([]);
  foodCategories$: Observable<foodCategory[]> = this.foodCategoriesSubject.asObservable();

  private activityCategoriesSubject = new BehaviorSubject<activityCategory[]>([]);
  activityCategories$: Observable<activityCategory[]> = this.activityCategoriesSubject.asObservable();

  private mobilityDataSubject = new BehaviorSubject<mobilityVehicle[]>([]);
  mobilityData$: Observable<mobilityVehicle[]> = this.mobilityDataSubject.asObservable();

  constructor(private http: HttpClient) {}

  /////////////////////////////////////////////////////////////
  // Food Data methods
  /////////////////////////////////////////////////////////////

  getFoodData() {
    return this.http.get<foodCategory[]>(`${this.foodDataUrl}`);
  }

  getUserFoodEntries() {
    return this.http.get<userFoodEntry[]>(`${this.foodEntriesUrl}`);
  }

  createUserFoodEntry(entry: userFoodEntry): Observable<userFoodEntry> {
    return this.http.post<userFoodEntry>(`${this.foodEntriesUrl}`, entry).pipe(
      tap(createdEntry => {
        const currentEntries = this.userFoodEntriesSubject.value;
        this.userFoodEntriesSubject.next([...currentEntries, createdEntry]);
      })
    );
  }

  updateUserFoodEntry(entry: userFoodEntry): Observable<userFoodEntry> {
    try {
      return this.http.patch<userFoodEntry>(`${this.foodEntriesUrl}/${entry.id}`, entry).pipe(
        tap(updatedEntry => {
          const currentEntries = this.userFoodEntriesSubject.value;
          const index = currentEntries.findIndex(e => e.id === updatedEntry.id);
          if (index !== -1) {
            currentEntries[index] = updatedEntry;
            this.userFoodEntriesSubject.next([...currentEntries]);
          }
        })
      );
    } catch (error) {
      console.error('Error updating food entry:', error);
      throw error; // Re-throw the error to be handled by the caller
    }
  }

  deleteUserFoodEntry(id: number): Observable<userFoodEntry> {
    return this.http.delete<userFoodEntry>(`${this.foodEntriesUrl}/${id}`).pipe(
      tap(() => {
        const currentEntries = this.userFoodEntriesSubject.value;
        this.userFoodEntriesSubject.next(currentEntries.filter(e => e.id !== id));
      })
    );
  }

  ////////////////////////////////////////////////////////////
  // Activity Data methods
  ////////////////////////////////////////////////////////////

  getActivityData() {
    return this.http.get<activityCategory[]>(`${this.activityDataUrl}`);
  }
  
  getUserActivityEntries() {
    return this.http.get<userActivityEntry[]>(`${this.activityEntriesUrl}`);
  }

  createUserActivityEntry(entry: userActivityEntry): Observable<userActivityEntry> {
    return this.http.post<userActivityEntry>(`${this.activityEntriesUrl}`, entry).pipe(
      tap(createdEntry => {
        const currentEntries = this.userActivityEntriesSubject.value;
        this.userActivityEntriesSubject.next([...currentEntries, createdEntry]);
      })
    );
  }

  updateUserActivityEntry(entry: userActivityEntry): Observable<userActivityEntry> {
    return this.http.patch<userActivityEntry>(`${this.activityEntriesUrl}/${entry.id}`, entry).pipe(
      tap(updatedEntry => {
        const currentEntries = this.userActivityEntriesSubject.value;
        const index = currentEntries.findIndex(e => e.id === updatedEntry.id);
        if (index !== -1) {
          currentEntries[index] = updatedEntry;
          this.userActivityEntriesSubject.next([...currentEntries]);
        }
      })
    );
  }

  deleteUserActivityEntry(id: number): Observable<userActivityEntry> {
    return this.http.delete<userActivityEntry>(`${this.activityEntriesUrl}/${id}`).pipe(
      tap(() => {
        const currentEntries = this.userActivityEntriesSubject.value;
        this.userActivityEntriesSubject.next(currentEntries.filter(e => e.id !== id));
      })
    );
  }

  ////////////////////////////////////////////////////////////
  // Mobility Data methods
  ////////////////////////////////////////////////////////////

  getMobilityData() {
    return this.http.get<mobilityVehicle[]>(`${this.mobilityDataUrl}`);
  }

  getUserMobilityEntries() {
    return this.http.get<userMobilityEntry[]>(`${this.mobilityEntriesUrl}`);
  }

  createUserMobilityEntry(entry: userMobilityEntry): Observable<userMobilityEntry> {
    return this.http.post<userMobilityEntry>(`${this.mobilityEntriesUrl}`, entry).pipe(
      tap(createdEntry => {
        const currentEntries = this.userMobilityEntriesSubject.value;
        this.userMobilityEntriesSubject.next([...currentEntries, createdEntry]);
      })
    );
  }

  updateUserMobilityEntry(entry: userMobilityEntry): Observable<userMobilityEntry> {
    return this.http.patch<userMobilityEntry>(`${this.mobilityEntriesUrl}/${entry.id}`, entry).pipe(
      tap(updatedEntry => {
        const currentEntries = this.userMobilityEntriesSubject.value;
        const index = currentEntries.findIndex(e => e.id === updatedEntry.id);
        if (index !== -1) {
          currentEntries[index] = updatedEntry;
          this.userMobilityEntriesSubject.next([...currentEntries]);
        }
      })
    );
  }

  deleteUserMobilityEntry(id: number): Observable<userMobilityEntry> {
    return this.http.delete<userMobilityEntry>(`${this.mobilityEntriesUrl}/${id}`).pipe(
      tap(() => {
        const currentEntries = this.userMobilityEntriesSubject.value;
        this.userMobilityEntriesSubject.next(currentEntries.filter(e => e.id !== id));
      })
    );
  }

  // Methods to load and update shared state
  loadAllData(): void {
    this.getUserFoodEntries().subscribe(entries => {
      this.userFoodEntriesSubject.next(entries);
    });

    this.getUserActivityEntries().subscribe(entries => {
      this.userActivityEntriesSubject.next(entries);
    });

    this.getUserMobilityEntries().subscribe(entries => {
      this.userMobilityEntriesSubject.next(entries);
    });

    this.getFoodData().subscribe(categories => {
      this.foodCategoriesSubject.next(categories);
    });

    this.getActivityData().subscribe(categories => {
      this.activityCategoriesSubject.next(categories);
    });

    this.getMobilityData().subscribe(mobilityData => {
      this.mobilityDataSubject.next(mobilityData);
    });
  }
}
